<template>
  <v-content
    v-if="opportunity && organization && !loading"
    class="client opportunity page"
    :style="organization.coverImage ? 'margin-top: -64px;' : 'margin-top: 10rem;'"
  >
    <Preview />

    <section>
      <v-img
        v-if="organization.coverImage"
        :src="organization.coverImage"
        :height="$vuetify.breakpoint.lgAndUp ? '500px' : '400px'"
        class="overlay overlay--navbar"
      />
    </section>

    <v-container v-if="opportunity && organization" class="pt-0">
      <v-layout column align-center>
        <v-flex class="text-xs-center" style="width: 66vw; max-width: 900px;">
          <v-card :class="{ 'card--overlap': organization.coverImage }">
            <v-layout column>
              <v-flex d-flex class="text-xs-center card--overlap__logo">
                <div>
                  <v-avatar size="164" :color="themes.candidate.light.muted">
                    <v-avatar size="162"> <img :src="organization.logo" alt="avatar" /> </v-avatar>
                  </v-avatar>
                </div>
              </v-flex>

              <v-flex d-flex class="text-xs-center">
                <v-card-title class="pb-1" style="height: 5rem;">
                  <v-spacer />
                  <OverviewEditor v-if="canEdit" :opportunity="opportunity" />
                </v-card-title>
              </v-flex>

              <v-flex d-flex class="text-xs-center">
                <v-card-text>
                  <div>
                    <div class="headline">{{ opportunity.name }}</div>
                    <div class="pt-1 subheading">
                      <a
                        @click="$router.push(`/organizations/${organization.id}`)"
                        :class="themes.candidate.light.mutedText"
                      >
                        {{ organization.name }}
                      </a>
                    </div>
                  </div>
                </v-card-text>
              </v-flex>

              <v-flex d-flex class="text-xs-center">
                <!-- Quick overview -->
                <v-card-text>
                  <v-layout row wrap align-center justify-space-around>
                    <div v-if="opportunity.locations.length" class="px-1">
                      <v-chip outline class="transparent">
                        <v-icon left class="text-vertical-middle">mdi-city</v-icon>
                        {{ opportunity.locations.length }}&nbsp;
                        {{ opportunity.locations.length | pluralize('Location') }}
                      </v-chip>
                    </div>
                    <div v-if="opportunity.totalIntake" class="px-1">
                      <v-chip outline class="transparent">
                        <v-icon left class="text-vertical-middle">mdi-account-multiple-plus</v-icon>
                        {{ opportunity.totalIntake }}&nbsp;positions
                      </v-chip>
                    </div>
                    <div v-if="opportunity.remuneration && opportunity.currency" class="px-1">
                      <v-chip outline class="transparent">
                        <v-icon left class="text-vertical-middle">mdi-coin</v-icon>
                        {{ opportunity.remuneration.amount | currency(opportunity.remuneration.currency) }}
                        {{ frequencyDescriptor(opportunity.remuneration.frequency) }}
                      </v-chip>
                    </div>
                  </v-layout>
                </v-card-text>
              </v-flex>

              <v-flex d-flex class="text-xs-center" v-if="canEdit" >
                <v-card-text>
                  <Publish :opportunity="opportunity" :organization="organization" />
                </v-card-text>
              </v-flex>
            </v-layout>
          </v-card>
        </v-flex>

        <!-- Line Break -->
        <v-flex v-if="opportunity.summary || opportunity.video" style="height: 32px" />

        <v-flex style="width: 66vw; max-width: 900px;">
          <v-card class="pt-4 px-4">
            <v-card-title class="title pb-1 pr-0">
              Opportunity Managers
              <v-spacer />
              <ManagersEditor :opportunity="opportunity" :edit="canEdit" />
            </v-card-title>

            <v-card-text>
              <v-chip
                outline
                v-for="(manager, i) in opportunity.managers"
                :key="`manager-position-${i}`"
                class="transparent"
              >
              <v-avatar>
                <img :src="manager.avatar || '/img/missing/avatars/original.png'" />
              </v-avatar>
                {{ manager.firstName }} {{ manager.lastName }}
              </v-chip>

              <v-chip outline v-if="!opportunity.managers[0]" class="transparent">
                <v-icon left class="text-vertical-middle">mdi-account-question</v-icon>
                No managers have been assigned to this opportunity.
              </v-chip>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Line Break -->
        <v-flex v-if="opportunity.summary || opportunity.video" style="height: 32px" />

        <v-flex
          v-if="opportunity.summary || opportunity.video"
          style="width: 66vw; max-width: 900px;"
        >
          <v-card class="pt-4 px-4">
            <v-card-title class="title pb-1 pr-0">
              About {{ opportunity.name }}
              <v-spacer />
              <SummaryEditor v-if="canEdit" :opportunity="opportunity" />
            </v-card-title>

            <v-card-text>
              <markdown
                v-if="opportunity.summary"
                :html="false"
                :source="opportunity.summary"
                class="markdown"
              />
              <v-chip outline v-else class="transparent">
                <v-icon left class="text-vertical-middle">mdi-information</v-icon>
                Please provide an opportunity summary
              </v-chip>
            </v-card-text>
            <v-card-text v-if="opportunity.video">
              <v-img class="pb-3">
                <v-layout justify-center>
                  <iframe
                    :width="$vuetify.breakpoint.mdAndUp ? 560 : 280"
                    :height="$vuetify.breakpoint.mdAndUp ? 315 : 158"
                    :src="opportunity.video"
                    allowfullscreen
                    frameborder="0"
                    allow="autoplay; encrypted-media"
                  />
                </v-layout>
              </v-img>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Line Break -->
        <v-flex
          v-if="
            (opportunity.locations && opportunity.locations.length) ||
              opportunity.opensAt ||
              opportunity.closesAt ||
              opportunity.commencesAt
          "
          style="height: 32px"
        />

        <v-flex
          v-if="
            (opportunity.locations && opportunity.locations.length) ||
              opportunity.opensAt ||
              opportunity.closesAt ||
              opportunity.commencesAt
          "
          style="width: 66vw; max-width: 900px;"
        >
          <v-card class="pt-4">
            <v-card-title class="title pb-1 pr-4">
              Locations and Dates
              <v-spacer />
              <LocationsAndDatesEditor
                v-if="canEdit"
                :opportunity="opportunity"
                :organization="organization"
              />
            </v-card-title>
            <v-img height="400px" v-if="opportunity.locations && opportunity.locations.length">
              <leaply-map :markers="opportunity.locations" width="100%" height="400px" />
            </v-img>
            <v-chip outline v-else class="transparent">
              <v-icon left class="text-vertical-middle">mdi-information</v-icon>
              Please provide opportunity locations
            </v-chip>
            <v-card-text
              v-if="opportunity.opensAt || opportunity.closesAt || opportunity.commencesAt"
            >
              <v-layout row wrap align-center justify-space-between class="mx-4">
                <v-chip outline v-if="opportunity.opensAt" class="transparent">
                  <v-icon small left>mdi-calendar-clock</v-icon>
                  Applications open: {{ new Date(opportunity.opensAt) | date("do MMM yyy") }}
                </v-chip>
                <v-chip outline v-if="opportunity.closesAt" class="transparent">
                  <v-icon small left>mdi-calendar-remove</v-icon>
                  Applications close: {{ new Date(opportunity.closesAt) | date("do MMM yyy") }}
                </v-chip>
                <v-chip outline v-if="opportunity.commencesAt" class="transparent">
                  <v-icon small left>mdi-calendar-check</v-icon>
                  Commencement date: {{ new Date(opportunity.commencesAt) | date("do MMM yyy") }}
                </v-chip>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Line Break -->
        <v-flex v-if="opportunity.offerings" style="height: 32px" />

        <v-flex v-if="opportunity.offerings" style="width: 66vw; max-width: 900px;">
          <v-card class="px-4 pt-4">
            <v-card-title class="title pb-1 pr-0">
              Key Offerings
              <v-spacer />
              <KeyOfferingsEditor v-if="canEdit" :opportunity="opportunity" />
            </v-card-title>
            <v-card-text>
              <v-container grid-list-lg fluid v-if="opportunity.offerings.length" class="pa-0">
                <v-layout row wrap align-start justify-start>
                  <v-flex xs6 sm4 md3 lg2
                    v-for="offering in opportunity.offerings" :key="`offering-${offering}`"
                  >
                    <div class="text-xs-center">
                      <div class="pb-2"><v-icon large>mdi-checkbox-marked-circle</v-icon></div>
                      <div class="caption" style="max-width: 120px; margin: auto;">
                        {{ offering }}
                      </div>
                    </div>
                  </v-flex>
                </v-layout>
              </v-container>
              <v-chip outline v-else class="transparent">
                <v-icon left class="text-vertical-middle">mdi-information</v-icon>
                No key offerings have been added
              </v-chip>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Line Break -->
        <v-flex v-if="opportunity.perks" style="height: 32px" />

        <v-flex v-if="opportunity.perks" style="width: 66vw; max-width: 900px;">
          <v-card class="px-4 pt-4">
            <v-card-title class="title pb-1 pr-0">
              Perks
              <v-spacer />
              <PerksEditor v-if="canEdit" :opportunity="opportunity" />
            </v-card-title>
            <v-card-text>
              <v-container grid-list-lg fluid
                v-if="opportunity.perks && opportunity.perks.length"
                class="pa-0"
              >
                <v-layout row wrap align-start justify-start>
                  <v-flex xs6 sm4 md3 lg2
                    v-for="perks in opportunity.perks" :key="`perks-${perks}`"
                  >
                    <div class="text-xs-center">
                      <div class="pb-2"><v-icon large>mdi-check-decagram</v-icon></div>
                      <div class="caption" style="max-width: 120px; margin: auto;">{{ perks }}</div>
                    </div>
                  </v-flex>
                </v-layout>
              </v-container>
              <v-chip outline v-else class="transparent">
                <v-icon left class="text-vertical-middle">mdi-information</v-icon>
                No perks have been added
              </v-chip>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Line Break -->
        <v-flex style="height: 32px" />

        <v-flex v-if="profileRequirements.length > 0 || qualificationRequirements.length > 0 || experienceRequirements.length > 0"
          style="width: 66vw; max-width: 900px;"
        >
          <v-card class="px-4 pt-4">
            <v-card-title class="title">
              <v-tooltip bottom :color="themes.client.light.tooltip">
                <span slot="activator">
                  <v-icon left class="text-vertical-bottom">mdi-information-outline</v-icon>
                  Requirements
                </span>
                <span> Required to be eligible to apply for this opportunity.</span>
              </v-tooltip>
              <v-spacer />
            </v-card-title>
            <v-card-text
              v-if="profileRequirements.length > 0 || qualificationRequirements.length > 0 || experienceRequirements.length > 0"
            >
              <RequirementGroup
                v-for="group in profileRequirements"
                :key="`profile-requirement-${group.id}`"
                :group="group"
                :first="group === profileRequirements[0]"
                :last="group === profileRequirements[profileRequirements.length - 1]"
              />
              <RequirementGroup
                v-for="group in qualificationRequirements"
                :key="`qualification-requirement-${group.id}`"
                :group="group"
                :first="group === qualificationRequirements[0]"
                :last="group === qualificationRequirements[qualificationRequirements.length - 1]"
              />
              <RequirementGroup
                v-for="group in experienceRequirements"
                :key="`experience-requirement-${group.id}`"
                :group="group"
                :first="group === experienceRequirements[0]"
                :last="group === experienceRequirements[experienceRequirements.length - 1]"
              />
            </v-card-text>
            <v-card-text v-else>
              No specific requirements have been set and this opportunity is open to anyone to
              apply.
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Line Break -->
        <v-flex
          v-if="
            opportunity.behavioralCompetencies.length > 0 ||
              (opportunity.technicalCompetencies && opportunity.technicalCompetencies.length > 0) ||
              (opportunity.professionalTools && opportunity.professionalTools.length > 0)
          "
          style="height: 32px"
        />

        <v-flex
          v-if="
            opportunity.behavioralCompetencies.length > 0 ||
              (opportunity.technicalCompetencies && opportunity.technicalCompetencies.length > 0) ||
              (opportunity.professionalTools && opportunity.professionalTools.length > 0)
          "
          style="width: 66vw; max-width: 900px;"
        >
          <v-card class="px-4 pt-4">
            <v-card-title class="title pb-1 pr-0">
              <v-tooltip bottom :color="themes.client.light.tooltip">
                <span slot="activator">
                  <v-icon left class="text-vertical-bottom">mdi-information-outline</v-icon>
                  Beneficial Skills
                </span>
                <span>
                  The following skills/tools are beneficial, but not necessary to qualify for the
                  opportunity.
                </span>
              </v-tooltip>
              <v-spacer />
              <SkillsEditor v-if="canEdit" :opportunity="opportunity" />
            </v-card-title>
            <v-card-text>
              <v-subheader
                v-if="
                  opportunity.behavioralCompetencies &&
                    opportunity.behavioralCompetencies.length > 0
                "
                class="pl-0"
              >
                Behavioral Competencies
              </v-subheader>
              <v-tooltip top
                v-for="bc in opportunity.behavioralCompetencies"
                :key="`trait-${bc.id}`"
              >
                <v-chip label small slot="activator" :class="themes.candidate.light.chip">
                  {{ bc.name }}
                </v-chip>
                <span>{{ bc.description }}</span>
              </v-tooltip>

              <v-subheader
                v-if="
                  opportunity.technicalCompetencies && opportunity.technicalCompetencies.length > 0
                "
                class="pl-0"
              >
                Technical Competencies
              </v-subheader>
              <v-chip label small
                v-for="tc in opportunity.technicalCompetencies"
                :key="tc"
                :class="themes.candidate.light.chip"
              >
                {{ tc }}
              </v-chip>

              <v-subheader
                v-if="opportunity.professionalTools && opportunity.professionalTools.length > 0"
                class="pl-0"
              >
                Professional Tools
              </v-subheader>
              <v-chip label small
                v-for="pt in opportunity.professionalTools"
                :key="pt"
                :class="themes.candidate.light.chip"
              >
                {{ pt }}
              </v-chip>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </v-content>
</template>

<script>
// Mixins
import {managedDocumentAttributes} from '@/mixins/managedDocumentAttributes.js'
// Components
import ManagersEditor from '@/components/client/opportunities/ManagersEditor'
import KeyOfferingsEditor from '@/components/client/opportunities/KeyOfferingsEditor'
import LocationsAndDatesEditor from '@/components/client/opportunities/LocationsAndDatesEditor'
import Markdown from 'vue-markdown'
import OverviewEditor from '@/components/client/opportunities/OverviewEditor'
import PerksEditor from '@/components/client/opportunities/PerksEditor'
import Preview from '@/components/client/opportunities/Preview'
import Publish from '@/components/client/opportunities/Publish'
import RequirementGroup from '@/components/guest/opportunities/RequirementGroup'
import SummaryEditor from '@/components/client/opportunities/SummaryEditor'
import SkillsEditor from '@/components/client/opportunities/SkillsEditor'

// Helpers
import _ from 'lodash'
import { mapGetters } from 'vuex'

export default {
  mixins: [managedDocumentAttributes],

  components: {
    ManagersEditor,
    KeyOfferingsEditor,
    LocationsAndDatesEditor,
    Markdown,
    OverviewEditor,
    PerksEditor,
    Preview,
    Publish,
    RequirementGroup,
    SkillsEditor,
    SummaryEditor,
  },
  created() {
    this.loading = true
    this.$store
      .dispatch('client/opportunities/fetch', this.$route.params.id)
      .then(() => {
        this.loading = false
        this.setDocumentAttributes()
      })
  },
  data() {
    return {
      preview: false,
      loading: false,
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
      organization: 'client/organization/model',
    }),

    opportunity() {
      return this.$store.getters['client/opportunities/find']()
    },

    canEdit() {
      if (!this.user || !this.opportunity.managers) return
      return this.user.primary || this.opportunity.managers.some(m => m.id === this.user.id)
    },

    profileRequirements() {
      if (this.eligibility && this.eligibility.requirementGroups) {
        return this.eligibility.requirementGroups.filter(
          group => group.type === 'ProfileRequirementGroup'
        )
      } else if (this.opportunity.requirementGroups) {
        return this.opportunity.requirementGroups.filter(
          group => group.type === 'ProfileRequirementGroup'
        )
      } else {
        return []
      }
    },

    qualificationRequirements() {
      if (this.eligibility && this.eligibility.requirementGroups) {
        return this.eligibility.requirementGroups.filter(
          group => group.type === 'ProfileQualificationRequirementGroup'
        )
      } else if (this.opportunity.requirementGroups) {
        return this.opportunity.requirementGroups.filter(
          group => group.type === 'ProfileQualificationRequirementGroup'
        )
      } else {
        return []
      }
    },

    experienceRequirements() {
      if (this.eligibility && this.eligibility.requirementGroups) {
        return this.eligibility.requirementGroups.filter(
          group => group.type === 'ExperienceRequirementGroup'
        )
      } else if (this.opportunity.requirementGroups) {
        return this.opportunity.requirementGroups.filter(
          group => group.type === 'ExperienceRequirementGroup'
        )
      } else {
        return []
      }
    },
  },
  methods: {
    setDocumentAttributes() {
      if (_.get(this, 'opportunity.name')) {
        this.documentAttributes.title = `${this.opportunity.name} - Leaply`
      } else {
        setTimeout(this.setDocumentAttributes, 100)
      }
    },
  },
}
</script>

<style lang="scss">
.client.opportunity.page {
  .overlay::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.2);
  }

  .overlay.overlay--navbar::after {
    height: 0;
  }

  @media (min-width: 960px) {
    .overlay.overlay--navbar::after {
      height: 64px;
    }
  }

  .card--overlap {
    margin-top: -3.9rem;
  }

  .card--overlap__logo {
    position: absolute;
    transform: translate(-50%, -70%);
    left: 50%;
  }
}
</style>
